import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius, appThemeShadow } from "styles/appThemeTokens";
import {
  TOOLBAR_BUTTON_LABEL_CLASS,
  toolbarIconOnlyButtonSx,
} from "common/Components/DataTable/tableToolbarButton.styles";
import { TAGS_UI } from "./NodeList.constants";

const uiFont = appThemeFonts.family.ui;

const EXPORT_MENU_MIN_WIDTH = 168;

const ExportToolbarButton = styled(Button)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 34,
  minHeight: 34,
  padding: "0 14px",
  backgroundColor: appThemeColors.background.paper,
  border: `1px solid ${appThemeColors.border.strong}`,
  borderRadius: appThemeRadius.pill,
  color: appThemeColors.text.primary,
  fontSize: "12.5px",
  fontWeight: 500,
  fontFamily: uiFont,
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  lineHeight: 1.2,
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
    color: appThemeColors.text.lightBlue,
    "& > *:nth-of-type(1)": {
      fontSize: 14,
    },
  },
  "& .MuiButton-endIcon": {
    marginRight: 0,
    marginLeft: 2,
    color: appThemeColors.text.muted,
    "& > *:nth-of-type(1)": {
      fontSize: 16,
    },
  },
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
    borderColor: appThemeColors.text.lightBlue,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: appThemeColors.background.paper,
    borderColor: appThemeColors.border.default,
    color: appThemeColors.text.faintLight,
    opacity: 0.55,
  },
  ...toolbarIconOnlyButtonSx,
}));

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

export default function NodeListExportCsvMenu({
  onSelect,
  disabled,
  disabledTitle,
  loading,
  options,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleButtonClick = useCallback(
    (event) => {
      if (disabled || loading) {
        return;
      }
      setAnchorEl(event.currentTarget);
    },
    [disabled, loading]
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOptionClick = useCallback(
    (value) => {
      handleMenuClose();
      onSelect?.(value);
    },
    [handleMenuClose, onSelect]
  );

  const button = (
    <ExportToolbarButton
      variant="outlined"
      size="small"
      startIcon={
        loading ? (
          <CircularProgress size={14} color="inherit" aria-hidden />
        ) : (
          <DownloadIcon aria-hidden />
        )
      }
      endIcon={loading ? null : <KeyboardArrowDownIcon aria-hidden />}
      onClick={handleButtonClick}
      disabled={disabled || loading}
      title={TAGS_UI.exportCsvLabel}
      aria-haspopup="menu"
      aria-expanded={menuOpen ? "true" : undefined}
      aria-controls={menuOpen ? "tags-export-csv-menu" : undefined}
      data-testid="tags-export-csv-button"
      aria-busy={loading || undefined}
    >
      <span className={TOOLBAR_BUTTON_LABEL_CLASS}>
        {loading ? TAGS_UI.exportCsvLoadingLabel : TAGS_UI.exportCsvLabel}
      </span>
    </ExportToolbarButton>
  );

  return (
    <>
      {!loading &&
      disabled &&
      typeof disabledTitle === "string" &&
      disabledTitle.trim() ? (
        <Tooltip title={disabledTitle} placement="top" arrow enterDelay={200}>
          <Box component="span" sx={{ display: "inline-block" }}>
            {button}
          </Box>
        </Tooltip>
      ) : (
        button
      )}
      <Menu
        id="tags-export-csv-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 0,
            "data-testid": "tags-export-source-menu",
            sx: {
              minWidth: anchorEl
                ? Math.max(EXPORT_MENU_MIN_WIDTH, anchorEl.offsetWidth)
                : EXPORT_MENU_MIN_WIDTH,
              marginTop: "5px",
              borderRadius: appThemeRadius.lg,
              border: `1px solid ${appThemeColors.border.default}`,
              boxShadow: appThemeShadow.menu,
              backgroundColor: appThemeColors.background.paper,
              fontFamily: uiFont,
            },
          },
        }}
      >
        {(options ?? []).map((option) => (
          <MenuItem
            key={option.value}
            disabled={loading}
            onClick={() => handleOptionClick(option.value)}
            data-testid={`tags-export-source-${option.value}`}
            sx={{
              fontFamily: uiFont,
              fontSize: "13px",
              borderRadius: appThemeRadius.md,
              mx: 0.5,
              my: 0.25,
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

NodeListExportCsvMenu.propTypes = {
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  disabledTitle: PropTypes.string,
  loading: PropTypes.bool,
  options: PropTypes.arrayOf(optionShape),
};

NodeListExportCsvMenu.defaultProps = {
  onSelect: undefined,
  disabled: false,
  disabledTitle: "",
  loading: false,
  options: [],
};
